import { ACTIVITY_TYPE, INSTRUCTOR_GENDERS, INSTRUCTOR_SCHOOLS, LANGUAGES, SEARCH_PARAMS, TERRAINS } from '../constants/searchConstants';
import { InstructorSearchResultProfile } from '../server/types';
import { SearchOptionTypes } from '../types/types';

export type SearchOption = {
	label: string,
	defaultValueText: string,
	helpText?: string | undefined,
	optionType: SearchOptionTypes,
	value?: string | number | number[], // Can be string from custom text, single number for index in valueLabels for SINGLE_FROM_RANGE, array of 2 indexes from values for range
	valueLabels: string[],
	queryParam: string
}

function quickSearchOptionsFactory(): SearchOption[] {
	return [{
			label: 'Group Size',
			defaultValueText: '1, 2 or 3',
			optionType: SearchOptionTypes.SINGLE_FROM_RANGE,
			valueLabels: ['1', '2', '3'],
			queryParam: SEARCH_PARAMS.GROUP_SIZE
		},{
			label: 'Instructor Level',
			defaultValueText: 'Level 1, 2, 3, 4',
			optionType: SearchOptionTypes.RANGE,
			valueLabels: ['Level 1', 'Level 2', 'Level 3', 'Level 4'],
			queryParam: SEARCH_PARAMS.INSTRUCTOR_LEVEL
		},{
			label: 'Ski School',
			defaultValueText: 'Search School',
			optionType: SearchOptionTypes.LIST,
			valueLabels: INSTRUCTOR_SCHOOLS,
			queryParam: SEARCH_PARAMS.SKI_SCHOOL
		},{
			label: 'Who',
			defaultValueText: 'Search Instructor',
			optionType: SearchOptionTypes.TEXT,
			valueLabels: [],
			queryParam: SEARCH_PARAMS.NAME
		}
	]
}

function subFilterOptionsFactory(): SearchOption[] {
	return [{
			label: 'Language',
			defaultValueText: 'Language',
			optionType: SearchOptionTypes.LIST,
			valueLabels: LANGUAGES,
			queryParam: SEARCH_PARAMS.LANGUAGE
		},{
			label: 'Gender',
			defaultValueText: 'Gender',
			optionType: SearchOptionTypes.MULTI_LIST,
			valueLabels: INSTRUCTOR_GENDERS,
			queryParam: SEARCH_PARAMS.GENDER
		},{
			label: 'Rating',
			defaultValueText: 'Rating',
			optionType: SearchOptionTypes.SINGLE_LIST,
			valueLabels: ['Good   [[icon-star-black]][[icon-star-black]][[icon-star-black]]+', 'Great   [[icon-star-black]][[icon-star-black]][[icon-star-black]][[icon-star-black]]+', 'Excellent   [[icon-star-black]][[icon-star-black]][[icon-star-black]][[icon-star-black]][[icon-star-black]]'],
			queryParam: SEARCH_PARAMS.RATING
		},{
			label: 'Age',
			defaultValueText: 'Age',
			optionType: SearchOptionTypes.RANGE,
			valueLabels: ['18', '20', '25', '30', '35', '40', '45', '50', '55', '60', '65+'],
			queryParam: SEARCH_PARAMS.AGE
		},{
			label: 'Terrain',
			defaultValueText: 'Terrain',
			optionType: SearchOptionTypes.MULTI_LIST,
			valueLabels: TERRAINS,
			queryParam: SEARCH_PARAMS.TERRAIN
		}
	]
}

// Search state data class
class SearchStateData {
	searchActivity: ACTIVITY_TYPE

	quickSearchOptions: SearchOption[]
	subFilterOptions: SearchOption[]

	searchResultProfiles: InstructorSearchResultProfile[]

	constructor() {
		this.searchActivity = ACTIVITY_TYPE.SKIING

		// this.gender = SEARCH_GENDERS.ANY
		this.quickSearchOptions = quickSearchOptionsFactory()
		this.subFilterOptions = subFilterOptionsFactory()

		this.searchResultProfiles = []
	}

	public clearAllQuickSearchOptions(): void {
		this.quickSearchOptions.forEach((option: SearchOption) => {
			option.value = undefined
		})
	}

	/**
	 * Method called to set a new value for the search activity
	 * will check if the new value is different and update search data accordingly 
	 * TODO: add the search update effect
	 */
	public setSearchActivity(value: ACTIVITY_TYPE): void {
		if (value !== this.searchActivity) {
			this.searchActivity = value
			// TODO: update data due to activity change
		}
	}

	/**
	 * Method to check if there are values in any of the quick search options, used to disable the search button when values change
	 * return: true if any quick values are set, false if all clear
	 */
	public areQuickSearchValuesSet(): boolean {
		// TODO: add check functionality
		let aValueIsSet: boolean = false
		this.quickSearchOptions.forEach((searchOption: SearchOption) => {
			if (searchOption.value !== undefined) {
				aValueIsSet = true
			}
		})
		return aValueIsSet
	}

	/**
	 * Method to add profiles to existing array of values
	 * @param profiles array of profiles from the server response
	 */
	public addSearchProfiles(profiles: InstructorSearchResultProfile[]) {
		// let shuffledProfiles: InstructorSearchResultProfile[] = []
		// TEMP: Shuffle Profiles to help randomize the first profile on home page, simple method before determining alternative method of selecting profile order
		for (let i: number = 0; i < profiles.length; i++) {
			const j: number = Math.floor(Math.random() * profiles.length)
			if (i !== j) {
				let tempProfile: InstructorSearchResultProfile = profiles[i]
				profiles[i] = profiles[j]
				profiles[j] = tempProfile
			}
		}

		this.searchResultProfiles = [...this.searchResultProfiles, ...profiles]
	}

	/**
	 * Method for clearing the current search profiles to make way for new search results
	 */
	public clearExistingProfiles(): void {
		// TODO: create method which will filter existing profiles with new profile list to allow for smoother clearing of results
		this.searchResultProfiles = []
	}
}

export const searchStateData: SearchStateData = new SearchStateData()